import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, Box, Button, Heading, TextButton } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyOwnershipVerificationWebApiHandler_startOrResume } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  isVerificationProcessing,
  OWNERSHIP_VERIFICATION_STATE_TAG,
  useOwnershipVerificationState,
} from '../../../../hooks/queries/useOwnershipVerification';
import { useVerificationCompletion } from '../../../../hooks/useVerificationCompletion';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
} from '../../../../styles/wizard';

import OwnershipVerificationStatus, { OWNERSHIP_VERIFICATION_ERROR_COPY } from '../../../OwnershipVerificationStatus';
import PersonaInquiry from '../../../PersonaInquiry';
import PersonaInquirySkeleton from '../../../PersonaInquiry/PersonaInquirySkeleton';
import PageWizard from '../../../Wizard/PageWizard';
import { useWizardNavigation } from '../../WizardNavigationProvider';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const VerifyOwnership = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const queryClient = useQueryClient();

  const { state, isVerified, isStartable, isStateLoading, isStateFetching, isStateError, refetchState } =
    useOwnershipVerificationState({
      listingAlias,
    });
  const { reportError, clearError } = useWizardNavigation();

  const {
    mutate: startVerification,
    data: inquiry,
    isPending,
    isError,
    isSuccess,
    reset,
  } = useRentalManagerUserPropertyOwnershipVerificationWebApiHandler_startOrResume({
    meta: {
      invalidates: [OWNERSHIP_VERIFICATION_STATE_TAG],
      context: { api: 'startOrResume', details: { listingAlias } },
    },
  });

  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  const [hasSdkError, setHasSdkError] = useState(false);

  const refetchVerificationState = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: ({ meta }) => Boolean(meta?.tags?.includes(OWNERSHIP_VERIFICATION_STATE_TAG)),
    });
  }, [queryClient]);

  // Separate from cancelling, which would leave the landlord at a neutral button with no idea
  // anything broke. Logged by hand because an SDK callback never reaches the query client.
  const handleSdkError = useCallback(
    (error: unknown) => {
      logError({ error, context: { api: 'personaInquiry', details: { listingAlias, inquiryId: inquiry?.inquiryId } } });
      setHasSdkError(true);
      reset();
    },
    [listingAlias, inquiry?.inquiryId, reset],
  );

  // A verified state makes this step inapplicable, so it has to move the landlord on itself.
  const settle = useVerificationCompletion({
    isVerified,
    onVerified: () => onGoToNextStep?.(),
    reset,
    refetchState: refetchVerificationState,
  });

  const retry = () => {
    setHasSdkError(false);
    startVerification({ postData: { listingAlias } });
  };

  // Hold Next until the server reports a verified state, matching the publish gate in ZRM-16878.
  useEffect(() => {
    if (!isVerified) {
      reportError();
      return () => clearError();
    }
  }, [isVerified, reportError, clearError]);

  // Once per mount, so backing out of Persona's flow doesn't relaunch it.
  useEffect(() => {
    if (!isStartable || hasAutoStarted) return;
    setHasAutoStarted(true);
    startVerification({ postData: { listingAlias } });
  }, [isStartable, hasAutoStarted, startVerification, listingAlias]);

  const inquiryId = inquiry?.inquiryId;
  // The endpoint answers 200 with an error body when it cannot mint an inquiry, so a resolved
  // mutation is not proof of one — and the generated DTO types `inquiryId` as always present.
  const hasFailed = isError || hasSdkError || (isSuccess && !inquiryId);
  // Attempted, idle, no inquiry: the landlord backed out of Persona's flow and needs a way in again.
  // Not while processing: that inquiry is already submitted, so resuming would hand it back.
  const canResume =
    hasAutoStarted && isStartable && !isPending && !hasFailed && !inquiryId && !isVerificationProcessing(state);

  // Order matters: a startable state outlives a failed request, so checking it earlier swallows both
  // the alert and the way out of this step.
  const renderStatus = () => {
    if (hasFailed) {
      return (
        <Alert
          tone="critical"
          data-testid="VerifyOwnershipError"
          actionButton={
            <TextButton onClick={retry} data-testid="BeginVerifyingButton">
              Try again
            </TextButton>
          }
        >
          {hasSdkError ? OWNERSHIP_VERIFICATION_ERROR_COPY.cannotLoad : OWNERSHIP_VERIFICATION_ERROR_COPY.cannotStart}
        </Alert>
      );
    }

    if (isStateError) {
      return (
        <Alert
          tone="critical"
          data-testid="VerifyOwnershipResultError"
          actionButton={
            <TextButton onClick={() => refetchState()} data-testid="RetryResultButton">
              Try again
            </TextButton>
          }
        >
          {OWNERSHIP_VERIFICATION_ERROR_COPY.cannotCheckStatus}
        </Alert>
      );
    }

    if (canResume) {
      return (
        <Button onClick={retry} data-testid="ResumeVerifyingButton">
          Continue verifying
        </Button>
      );
    }

    // The container filters out every state without a settled outcome, so the fallback only fires if
    // the backend adds one.
    return (
      <OwnershipVerificationStatus
        state={state}
        fallback={
          <Alert
            tone="info"
            data-testid="VerifyOwnershipUnknown"
            actionButton={
              <TextButton onClick={() => refetchState()} data-testid="CheckVerificationStatusButton">
                Check again
              </TextButton>
            }
          >
            Verification is still in progress
          </Alert>
        }
      />
    );
  };

  // Not just `inquiryId`: reset() clears it a render later, so a broken frame would linger.
  const frameInquiryId = hasSdkError ? undefined : inquiryId;
  // `!inquiryId` because a background refetch must never unmount a flow the landlord is inside.
  const isLaunching = !hasFailed && !inquiryId && (isPending || isStateLoading || isStateFetching);
  const isPersonaOnScreen = isLaunching || Boolean(frameInquiryId);

  const renderBody = () => {
    // Outside the narrower section the last branch uses, so it doesn't shift when the frame lands.
    if (isLaunching) {
      return <PersonaInquirySkeleton />;
    }

    if (frameInquiryId) {
      return (
        <PersonaInquiry
          inquiryId={frameInquiryId}
          sessionToken={inquiry?.sessionToken}
          onComplete={settle}
          onCancel={settle}
          onError={handleSdkError}
        />
      );
    }

    return (
      <Box css={wizardFormSectionContainerStyles.raw()}>
        <Heading level={2} textStyle="heading-sm" css={wizardHeadingStyles}>
          Verify your identity
        </Heading>

        {renderStatus()}
      </Box>
    );
  };

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      hideNextButton={isPersonaOnScreen}
      hideJumpToBottomButton={isPersonaOnScreen}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
    >
      <Box css={wizardFlexContainerStyles} data-testid="VerifyOwnership">
        <Box css={{ width: '100%' }}>{renderBody()}</Box>
      </Box>
    </PageWizard>
  );
};

export default VerifyOwnership;
